import { Flex, Text } from '@radix-ui/themes';
import React, { useEffect, useState } from 'react';
import BidHistoryGroup from './BidHistoryGroup';
import { Button } from '@/app/components/ui/button';
import { cn } from '@/lib/utils';
import PersonalBidHistoryRow from './PersonalBidHistoryRow';
import { Separator } from '@/app/components/ui/separator';
import ToggleFlagged from './ToggleFlagged';
import { useLocalStorage } from '@/app/hooks/useLocalStorage';
import { AuctionState, Bid, BidType } from '@prisma/client';
import { useParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import useSWR from 'swr';
import DoubleBidTooltip from './DoubleBidTooltip';
import { isBefore } from 'date-fns';
import { NoncedBid, sortBids } from './util';

const LOT_BID_HISTORY_TABS = {
  ALL: "ALL",
  PERSONAL: "PERSONAL"
}

export const LOT_BID_HISTORY_QUERY_KEY = "lotBidHistory";
export const LOT_BID_HISTORY_MAX_BID_QUERY_KEY = "lotBidMaxBid";
const SHOW_LOT_HISTORY_FRAUDULENT_BIDS_KEY = "SHOW_LOT_HISTORY_FRAUDULENT_BIDS_KEY";
const REFETCH_INTERVAL = 5 * 1000;

interface Props {
  auctionState: AuctionState;
  showPersonalTab?: boolean;
  auctionId: string;
  lotId: string;
}

const LotBidHistory = ({ auctionState, showPersonalTab = true, auctionId, lotId }: Props) => {
  const { data: session }: any = useSession();
  const [showAll, setShowAll] = useState(false);
  const [activeTab, setActiveTab] = useState(LOT_BID_HISTORY_TABS.ALL);
  // const [showFlagged, setShowFlagged] = useLocalStorage(
  //   SHOW_LOT_HISTORY_FRAUDULENT_BIDS_KEY,
  //   false
  // );
  const [showFlagged, setShowFlagged] = useState(false);

  // All data
  const [bids, setBids] = useState<NoncedBid[]>([]);
  // The data that appears on the UI (possibly omits flagged bids).
  const [rows, setRows] = useState<NoncedBid[]>([]);
  const [existsFlaggedBids, setExistsFlaggedBids] = useState(false);

  const { data: queriedBids, error, isLoading } = useSWR<NoncedBid[]>(
    [LOT_BID_HISTORY_QUERY_KEY, auctionId, lotId],
    async () => {
      if (!auctionId || !lotId) return Promise.resolve([]);
      return fetch(`/api/auctions/${auctionId.toString()}/lots/${lotId.toString()}/bids?`
        + new URLSearchParams({
          nonce: 'true',
          type: BidType.STANDARD
        }))
        .then(res => res.json());
    },
  );

  // Does not need to be refetched on interval --
  // We can simply invalidate on max bid.
  const { data: maxBids } = useSWR<Bid[]>(
    showPersonalTab ? [LOT_BID_HISTORY_MAX_BID_QUERY_KEY, auctionId, lotId] : null,
    async () => {
      if (!auctionId || !lotId) return Promise.resolve([]);
      return fetch(`/api/auctions/${auctionId.toString()}/lots/${lotId.toString()}/maxBid?limit=1`)
        .then(res => res.json());
    },
  );

  useEffect(() => {
    if (activeTab === LOT_BID_HISTORY_TABS.ALL) {
      setRows(showFlagged ? bids : bids.filter(b => !b.removed));
      setExistsFlaggedBids(bids.findIndex(bid => bid.removed) >= 0);
    } else if (activeTab === LOT_BID_HISTORY_TABS.PERSONAL) {
      setRows(bids.filter(b => b.userId === session?.userId && !b.removed));
    }
  }, [activeTab, bids, showFlagged, session?.userId]);

  // Set currentHighest for the appropriate bid.
  useEffect(() => {
    if (queriedBids && queriedBids.length > 0) {
      // Find all non-removed bids
      const validBids = queriedBids.filter(bid => !bid.removed);

      if (validBids.length > 0) {
        setExistsFlaggedBids(queriedBids.some(bid => bid.removed));

        // Find the highest bid amount among valid bids
        const highestAmount = Math.max(...validBids.map(bid => bid.amount));

        // Find the earliest bid with the highest amount
        const highestBids = validBids.filter(bid => bid.amount === highestAmount);
        const earliestHighestBid = highestBids.reduce((earliest, current) =>
          isBefore(new Date(current.createdAt), new Date(earliest.createdAt)) ? current : earliest,
          highestBids[0]
        );

        // Create augmented data with the highest bid marked
        const augmentedData: NoncedBid[] = queriedBids.map(bid => ({
          ...bid,
          currentHighest: bid.id === earliestHighestBid.id
        }));

        setBids(sortBids(augmentedData));
      } else {
        setExistsFlaggedBids(true);
        setBids(queriedBids);
      }
    }
  }, [queriedBids]);

  const toggleFlagged = () => {
    setShowFlagged((show: boolean) => {
      setRows(show ? bids.filter(b => !b.removed) : bids);
      return !show;
    });
  }

  // The ellipsis controls whether the user can see removed bids.
  const showEllipsis = false; //activeTab === LOT_BID_HISTORY_TABS.ALL && existsFlaggedBids;
  const showDoubleBidTooltip = bids.length !== new Set(bids.map(bid => bid.amount)).size && activeTab === LOT_BID_HISTORY_TABS.ALL;

  return (
    <Flex direction="column" className="w-full text-xs sm:text-sm">
      <Flex className="w-full mb-5" align="center" justify="between">
        <Flex className="space-x-2">
          {(showPersonalTab
            ? [
                { name: LOT_BID_HISTORY_TABS.ALL, label: "Bid History" },
                { name: LOT_BID_HISTORY_TABS.PERSONAL, label: "My Bids" },
              ]
            : [
                { name: LOT_BID_HISTORY_TABS.ALL, label: "Bid History" },
              ]
            ).map((tab, idx) => (
            <Button
              key={idx}
              variant="outline"
              className={cn(
                "rounded-2xl border",
                activeTab === tab.name ?
                  'hover:bg-white' :
                  'bg-zinc-100 text-muted-foreground hover:bg-zinc-100',
              )}
              onClick={() => setActiveTab(tab.name)}
            >
              {tab.label}
            </Button>
          ))}
        </Flex>
        {(showEllipsis || showDoubleBidTooltip) &&
          <Flex className="space-x-2" align="center">
            {showEllipsis &&
              <ToggleFlagged show={showFlagged} toggle={toggleFlagged} />
            }
            {showDoubleBidTooltip &&
              <DoubleBidTooltip />
            }
          </Flex>
        }
      </Flex>
      {showPersonalTab && activeTab === LOT_BID_HISTORY_TABS.PERSONAL && maxBids && maxBids.length > 0 &&
        <>
          <PersonalBidHistoryRow
            bid={{ ...maxBids[0], currentHighest: false }}
          />
          <Separator className="my-2" />
        </>
      }
      <BidHistoryGroup
        bids={rows}
        showAll={showAll}
        setShowAll={setShowAll}
        personal={showPersonalTab && activeTab === LOT_BID_HISTORY_TABS.PERSONAL}
        loading={isLoading}
        currentUserId={session?.userId}
        auctionState={auctionState}
      />
      {showFlagged && activeTab === LOT_BID_HISTORY_TABS.ALL && rows.some(r => r.removed) &&
        <Flex className="px-2 mt-4 text-sm">
          <Text>This lot contains removed bids. <Link href='/fraud-protection' className="text-blue-700">Learn more</Link>.</Text>
        </Flex>
      }
    </Flex >
  )
};


export default LotBidHistory
